/**
 * Phase 5 — Special Events picker with per-event Extras (Refinement 3).
 *
 * Chip multi-select drives which library special events apply to this day.
 * Each selected event renders an inline detail panel with:
 *   - location_override + comment (existing)
 *   - NEW Extras section: list of `{title, items[]}` sub-sections — canapés,
 *     wine pairings, setup notes, etc. Each card has Title + Items textarea
 *     + remove button. "+ Add section" appends a new empty card.
 *
 * Free-text events (no library id) are not surfaced here; that's intentional
 * for now because the picker drives off library names.
 */
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  PartyPopper, Plus, X, ChevronDown, ChevronRight, Cookie,
} from "lucide-react";
import { arrToText, textToArr } from "@/components/mealplan/textListHelpers";
import client from "@/apiClient";

const UNGROUPED_KEY = "__ungrouped__";
const ALL_GROUPS_KEY = "__all__";

export default function SpecialEventsPicker({
  value,
  onChange,
  specialEventsLib,
}) {
  const list = useMemo(() => value || [], [value]);

  const selectedIds = useMemo(
    () => new Set(list.map((e) => e.special_event_id).filter(Boolean)),
    [list]
  );

  // Phase 12 — load groups lazily; the picker still works if the fetch
  // fails (everything just falls under "Ungrouped").
  const [groupsLib, setGroupsLib] = useState([]);
  useEffect(() => {
    let cancelled = false;
    client
      .get("/lib/special_event_groups")
      .then((r) => {
        if (!cancelled) setGroupsLib(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setGroupsLib([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Phase 12 — load canapés library lazily so every special event card
  // can offer the optional Canapé Items section without round-trips per
  // panel mount. Soft-fail (empty list) on error.
  const [canapesLib, setCanapesLib] = useState([]);
  useEffect(() => {
    let cancelled = false;
    client
      .get("/lib/canapes")
      .then((r) => {
        if (!cancelled) setCanapesLib(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setCanapesLib([]);
      });
    return () => { cancelled = true; };
  }, []);

  const groupsById = useMemo(() => {
    const m = new Map();
    for (const g of groupsLib) m.set(g.id, g);
    return m;
  }, [groupsLib]);

  // Phase 12 — group filter + per-group collapse state. Both live in
  // local state (not persisted), per spec.
  const [filterGroupId, setFilterGroupId] = useState(ALL_GROUPS_KEY);
  const [collapsed, setCollapsed] = useState(() => new Set());

  // Bucket the library events by `group_id` (or UNGROUPED_KEY).
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const lib of (specialEventsLib || [])) {
      const k = lib.group_id || UNGROUPED_KEY;
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(lib);
    }
    // Order: groups in their library order, ungrouped last.
    const ordered = [];
    for (const g of groupsLib) {
      const items = buckets.get(g.id) || [];
      if (items.length) ordered.push({ groupId: g.id, group: g, items });
    }
    const un = buckets.get(UNGROUPED_KEY) || [];
    if (un.length) ordered.push({ groupId: UNGROUPED_KEY, group: null, items: un });
    return ordered;
  }, [specialEventsLib, groupsLib]);

  // Filter according to the dropdown selection.
  const visibleGroups = useMemo(() => {
    if (filterGroupId === ALL_GROUPS_KEY) return grouped;
    return grouped.filter((bucket) => bucket.groupId === filterGroupId);
  }, [grouped, filterGroupId]);

  const toggleCollapsed = (groupId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const toggle = (lib) => {
    if (selectedIds.has(lib.id)) {
      onChange(list.filter((e) => e.special_event_id !== lib.id));
    } else {
      const g = lib.group_id ? groupsById.get(lib.group_id) : null;
      onChange([
        ...list,
        {
          special_event_id: lib.id,
          name: lib.name,
          location_override: null,
          // Phase 7++ — snapshot default_start / default_end from the lib
          // doc when adding. The user can override per-day in the inputs
          // below; this saves typing for routine recurring events.
          start: lib.default_start || null,
          end: lib.default_end || null,
          comment: "",
          extras: [],
          // Phase 12 — snapshot the group info into the per-day event
          // so the PDF badge survives lib deletion.
          group_id: lib.group_id || null,
          group_name: g ? g.name : null,
        },
      ]);
    }
  };

  const patchEvent = (id, patch) => {
    onChange(
      list.map((e) =>
        e.special_event_id === id ? { ...e, ...patch } : e
      )
    );
  };

  const setExtras = (id, extras) => patchEvent(id, { extras });
  // Phase 12 — canapé items live as a first-class section on every
  // special event card. Free-text events without an id fall back to
  // `name` for the lookup key (same scheme `EventDetailPanel` uses).
  const setCanapeItems = (id, canape_items) =>
    patchEvent(id, { canape_items });

  return (
    <section
      className="rounded-lg border bg-card p-4"
      data-testid="special-events-picker"
    >
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        <PartyPopper className="h-3.5 w-3.5" />
        Special Events
      </h3>

      {/* Phase 12 — Group filter dropdown */}
      {(groupsLib.length > 0 || grouped.some((b) => b.groupId === UNGROUPED_KEY)) && (
        <div className="mb-3 flex items-center gap-2" data-testid="special-event-group-filter">
          <Label className="text-xs text-muted-foreground">Group</Label>
          <Select value={filterGroupId} onValueChange={setFilterGroupId}>
            <SelectTrigger className="h-7 w-[200px] text-xs" data-testid="group-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_GROUPS_KEY}>All Groups</SelectItem>
              {groupsLib.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
              <SelectItem value={UNGROUPED_KEY}>Ungrouped</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Phase 12 — Collapsible group sections */}
      <div className="mb-3 space-y-2">
        {visibleGroups.map(({ groupId, group, items }) => {
          const isCollapsed = collapsed.has(groupId);
          const groupLabel = group ? group.name : "Ungrouped";
          const groupColor = group?.color || "#94A3B8";
          return (
            <div
              key={groupId}
              className="rounded border bg-background"
              data-testid={`special-event-group-${groupId}`}
            >
              <button
                type="button"
                onClick={() => toggleCollapsed(groupId)}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent"
                data-testid={`group-header-${groupId}`}
              >
                {isCollapsed
                  ? <ChevronRight className="h-3.5 w-3.5" />
                  : <ChevronDown className="h-3.5 w-3.5" />}
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: groupColor }}
                />
                <span className="font-semibold">{groupLabel}</span>
                <span className="text-muted-foreground">({items.length})</span>
              </button>
              {!isCollapsed && (
                <div className="flex flex-wrap gap-1.5 px-2 pb-2">
                  {items.map((lib) => {
                    const sel = selectedIds.has(lib.id);
                    return (
                      <button
                        key={lib.id}
                        type="button"
                        onClick={() => toggle(lib)}
                        data-testid={`special-event-chip-${lib.id}`}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                          sel
                            ? "border-violet-400 bg-violet-100 text-violet-900"
                            : "border-input bg-background hover:bg-accent"
                        )}
                      >
                        {lib.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {visibleGroups.length === 0 && (
          <p className="px-2 text-xs italic text-muted-foreground">
            No events in this group.
          </p>
        )}
      </div>

      {/* Selected event detail panels */}
      <div className="space-y-3">
        {list.length === 0 && (
          <p className="text-xs italic text-muted-foreground">
            No special events for this day. Click a chip above to add one.
          </p>
        )}
        {list.map((evt) => {
          // Phase 15 (F1) — resolve the library doc for this event so
          // we can surface its `linked_canape_ids` as suggestion chips.
          const libDoc = evt.special_event_id
            ? (specialEventsLib || []).find((l) => l.id === evt.special_event_id)
            : null;
          const linkedCanapeIds = libDoc?.linked_canape_ids || [];
          return (
            <EventDetailPanel
              key={evt.special_event_id || evt.name}
              evt={evt}
              group={evt.group_id ? groupsById.get(evt.group_id) : null}
              canapesLib={canapesLib}
              linkedCanapeIds={linkedCanapeIds}
              onChange={(patch) => patchEvent(evt.special_event_id, patch)}
              onChangeExtras={(extras) => setExtras(evt.special_event_id, extras)}
              onChangeCanapeItems={(items) =>
                setCanapeItems(evt.special_event_id, items)
              }
            />
          );
        })}
      </div>
    </section>
  );
}

function EventDetailPanel({
  evt,
  group,
  canapesLib,
  // Phase 15 (F1) — ids from `lib_special_events.linked_canape_ids`.
  // When this list is non-empty AND the day's event has no canapés
  // yet, we show a "Suggested canapés" chip row with a one-click
  // "+ Add to today's canapé function" button that copies them as
  // canape_items.
  linkedCanapeIds = [],
  onChange,
  onChangeExtras,
  onChangeCanapeItems,
}) {
  const extras = evt.extras || [];
  const canapeItems = evt.canape_items || [];
  const eid = evt.special_event_id || "free";

  const addExtra = () => onChangeExtras([...extras, { title: "", items: [] }]);
  const removeExtra = (idx) =>
    onChangeExtras(extras.filter((_, i) => i !== idx));
  const updateExtra = (idx, patch) =>
    onChangeExtras(
      extras.map((x, i) => (i === idx ? { ...x, ...patch } : x))
    );

  const _newItemKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const addCanapeItem = () =>
    onChangeCanapeItems([
      ...canapeItems,
      {
        _key: _newItemKey(),
        canape_id: null,
        name: "",
        quantity: 0,
        production_area_name: "",
        notes: "",
      },
    ]);
  const updateCanapeItem = (idx, patch) =>
    onChangeCanapeItems(
      canapeItems.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    );
  const removeCanapeItem = (idx) =>
    onChangeCanapeItems(canapeItems.filter((_, i) => i !== idx));
  const pickCanapeFromLib = (idx, libId) => {
    if (libId === "__custom__") {
      updateCanapeItem(idx, {
        canape_id: null, name: "", production_area_name: "",
      });
      return;
    }
    const lib = (canapesLib || []).find((c) => c.id === libId);
    if (!lib) return;
    updateCanapeItem(idx, {
      canape_id: lib.id,
      name: lib.name,
      production_area_name: lib.production_area_name || "",
    });
  };

  // Phase 12 — group badge color falls back to slate when the group lib
  // doc has been deleted (drift-tolerant) or never set a custom color.
  const badgeColor = group?.color || "#94A3B8";

  return (
    <div
      className="rounded-md border border-violet-200 bg-violet-50/40 p-3"
      data-testid={`special-event-card-${eid}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-semibold">{evt.name}</span>
        {(group?.name || evt.group_name) && (
          <span
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
            style={{
              backgroundColor: `${badgeColor}1A`,
              borderColor: `${badgeColor}66`,
              color: badgeColor,
            }}
            data-testid={`special-event-group-badge-${eid}`}
            title="Special event group"
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: badgeColor }}
            />
            {group?.name || evt.group_name}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Start
          </Label>
          <Input
            type="time"
            value={evt.start || ""}
            onChange={(e) => onChange({ start: e.target.value || null })}
            className="h-7 text-xs"
            data-testid={`special-event-start-${eid}`}
          />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            End
          </Label>
          <Input
            type="time"
            value={evt.end || ""}
            onChange={(e) => onChange({ end: e.target.value || null })}
            className="h-7 text-xs"
            data-testid={`special-event-end-${eid}`}
          />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Location override
          </Label>
          <Input
            value={evt.location_override || ""}
            onChange={(e) =>
              onChange({ location_override: e.target.value || null })
            }
            placeholder="(use library default)"
            className="h-7 text-xs"
            data-testid={`special-event-location-${eid}`}
          />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Comment
          </Label>
          <Input
            value={evt.comment || ""}
            onChange={(e) => onChange({ comment: e.target.value })}
            placeholder="Optional comment"
            className="h-7 text-xs"
            data-testid={`special-event-comment-${eid}`}
          />
        </div>
      </div>

      {/* Phase 15 (B3) — gate the full amber "Canapé Items" panel on
          either: (a) the day's event already has canape_items attached,
          OR (b) the library doc has linked_canape_ids that we offer as
          a suggestion. Empty + unlinked → tiny "+ Add canapés" button
          only. This stops the phantom Canapé Function block from
          rendering inside groups with no events / events with no canapés.
          The dedicated top-level Canapé Function section (its own
          collapsible) is unaffected — it lives outside Special Events.
      */}
      {canapeItems.length === 0 && linkedCanapeIds.length > 0 && (
        // Phase 15 (F1) — suggestion chip row. Source: the lib event's
        // linked_canape_ids. One-click button copies them as canape_items.
        <div
          className="mt-3 rounded-md border border-dashed border-amber-300 bg-amber-50/40 p-2.5"
          data-testid={`special-event-canape-suggestions-${eid}`}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <Cookie className="h-3.5 w-3.5 text-amber-700" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
              Suggested canapés:
            </span>
            {linkedCanapeIds.map((cid) => {
              const lib = (canapesLib || []).find((c) => c.id === cid);
              return (
                <span
                  key={cid}
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                  data-testid={`special-event-canape-chip-${eid}-${cid}`}
                >
                  {lib?.name || "(canapé removed)"}
                </span>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const items = linkedCanapeIds
                  .map((cid) => (canapesLib || []).find((c) => c.id === cid))
                  .filter(Boolean)
                  .map((lib) => ({
                    _key: _newItemKey(),
                    canape_id: lib.id,
                    name: lib.name,
                    quantity: 0,
                    production_area_name: lib.production_area_name || "",
                    notes: "",
                  }));
                if (items.length) onChangeCanapeItems(items);
              }}
              className="ml-1 h-6 border-amber-400 bg-white text-[10px] font-semibold text-amber-900 hover:bg-amber-100"
              data-testid={`special-event-canape-add-from-lib-${eid}`}
            >
              <Plus className="h-3 w-3" />
              Add to today's canapé function
            </Button>
          </div>
        </div>
      )}

      {canapeItems.length === 0 && linkedCanapeIds.length === 0 && (
        // Empty state — no panel, just a small inline link to add
        // a canapé row. Avoids the phantom Canapé Function card.
        <div className="mt-2 text-right">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addCanapeItem}
            className="h-6 text-[10px] font-medium text-amber-700 hover:bg-amber-50 hover:text-amber-900"
            data-testid={`special-event-canape-add-empty-${eid}`}
          >
            <Cookie className="h-3 w-3" />
            <Plus className="h-3 w-3" />
            Add canapés to this event
          </Button>
        </div>
      )}

      {canapeItems.length > 0 && (
      /* Phase 12 — optional Canapé Items section on every special event card.
         Empty by default; lets the user attach canapés without flipping the
         dedicated `is_canape_function` preset. Backend PDF endpoints already
         surface these on the Canapé Function and filtered PDFs.
         Rendered ABOVE Extras so the affordance is the first thing the
         user notices on each card.
         Phase 15 (B3) — only mounts when canape_items has rows; empty state
         falls through to the suggestion / "+ Add" affordances above. */
      <div
        className="mt-3 rounded-md border-2 border-amber-300 bg-amber-50/60 p-3"
        data-testid={`special-event-canapes-${eid}`}
      >
        <div className="mb-2 flex items-center justify-between">
          <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-800">
            <Cookie className="h-3.5 w-3.5" />
            Canapé Items
            {canapeItems.length > 0 && (
              <span className="ml-1 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                {canapeItems.length}
              </span>
            )}
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCanapeItem}
            className="h-7 border-amber-400 bg-white text-xs font-semibold text-amber-900 hover:bg-amber-100"
            data-testid={`special-event-canape-add-${eid}`}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Canapé Items
          </Button>
        </div>

        {canapeItems.length === 0 ? (
          <p className="text-[11px] italic text-muted-foreground">
            No canapés on this event yet. Click <strong>Add Canapé Items</strong>
            {" "}to attach prep rows (name, quantity, production area, notes)
            for the kitchen team — works on any special event, not just
            dedicated Canapé Functions.
          </p>
        ) : (
          <div className="space-y-2">
            {canapeItems.map((it, idx) => {
              const lib = (canapesLib || []).find((c) => c.id === it.canape_id);
              return (
                <div
                  key={it._key || `c-item-${idx}`}
                  className="rounded border bg-white p-2"
                  data-testid={`special-event-canape-item-${eid}-${idx}`}
                >
                  <div className="mb-2 flex items-center gap-1.5">
                    {lib?.photo_url ? (
                      <img
                        src={lib.photo_url}
                        alt={lib.name}
                        className="h-8 w-8 rounded border object-cover"
                      />
                    ) : null}
                    <Select
                      value={it.canape_id || "__custom__"}
                      onValueChange={(v) => pickCanapeFromLib(idx, v)}
                    >
                      <SelectTrigger
                        className="h-7 flex-1 text-xs"
                        data-testid={`special-event-canape-picker-${eid}-${idx}`}
                      >
                        <SelectValue placeholder="Pick a canapé or type a name…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom__">
                          (Type a one-off name)
                        </SelectItem>
                        {(canapesLib || []).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCanapeItem(idx)}
                      className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10"
                      data-testid={`special-event-canape-remove-${eid}-${idx}`}
                      aria-label="Remove canapé"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                    <Input
                      value={it.name || ""}
                      placeholder="Canapé name"
                      onChange={(e) => updateCanapeItem(idx, { name: e.target.value })}
                      className="h-7 text-xs"
                      data-testid={`special-event-canape-name-${eid}-${idx}`}
                    />
                    <Input
                      type="number"
                      min={0}
                      value={Number.isFinite(it.quantity) ? it.quantity : 0}
                      onChange={(e) => updateCanapeItem(idx, {
                        quantity: Math.max(
                          0, parseInt(e.target.value || "0", 10) || 0,
                        ),
                      })}
                      placeholder="Quantity"
                      className="h-7 text-xs"
                      data-testid={`special-event-canape-qty-${eid}-${idx}`}
                    />
                    <Input
                      value={it.production_area_name || ""}
                      placeholder="Production area"
                      onChange={(e) => updateCanapeItem(idx, {
                        production_area_name: e.target.value,
                      })}
                      className="h-7 text-xs"
                      data-testid={`special-event-canape-area-${eid}-${idx}`}
                    />
                  </div>
                  <Textarea
                    value={it.notes || ""}
                    placeholder="Notes (VIP, allergens, …)"
                    rows={1}
                    onChange={(e) => updateCanapeItem(idx, { notes: e.target.value })}
                    className="mt-1.5 text-xs"
                    data-testid={`special-event-canape-notes-${eid}-${idx}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <div
        className="mt-3 rounded-md border border-violet-200 bg-white p-2"
        data-testid={`special-event-extras-${eid}`}
      >
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-violet-800">
            Extras
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addExtra}
            className="h-6 text-[10px]"
            data-testid={`special-event-extra-add-${eid}`}
          >
            <Plus className="h-3 w-3" />
            Add section
          </Button>
        </div>

        {extras.length === 0 ? (
          <p className="text-[11px] italic text-muted-foreground">
            No extras yet. Use "Add section" for things like wine pairings or
            setup notes.
          </p>
        ) : (
          <div className="space-y-2">
            {extras.map((x, idx) => (
              <div
                key={idx}
                className="rounded-md border bg-slate-50/40 p-2"
              >
                <div className="flex items-center gap-1.5">
                  <Input
                    value={x.title || ""}
                    onChange={(e) =>
                      updateExtra(idx, { title: e.target.value })
                    }
                    placeholder="Section title (e.g. Wine Pairings)"
                    className="h-7 text-xs font-medium"
                    data-testid={`special-event-extra-title-${eid}-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeExtra(idx)}
                    className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10"
                    data-testid={`special-event-extra-remove-${eid}-${idx}`}
                    aria-label="Remove extra section"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Textarea
                  value={arrToText(x.items)}
                  onChange={(e) =>
                    updateExtra(idx, { items: textToArr(e.target.value) })
                  }
                  placeholder="One item per line"
                  rows={3}
                  className="mt-1.5 text-xs"
                  data-testid={`special-event-extra-items-${eid}-${idx}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
